import { Component, ElementRef, Inject, ViewChild } from '@angular/core';
import { CdkDragDrop, CdkDragMove, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  CLARI_CHAT_MODEL_CATEGORIES,
  ClariChatModelCategory,
  normalizeFlamePickerModelId
} from 'src/app/shared/clari-chat-model-catalog';

export interface ComparePickerModel {
  id: string;
  name: string;
}

const EXCLUDED_MODEL_IDS = new Set(['chatgpt54', 'minichatgpt54']);

@Component({
  selector: 'app-model-select-dialog',
  templateUrl: './model-selection-dialog.component.html',
  styleUrls: ['./model-selection-dialog.component.scss']
})
export class ModelSelectionDialogComponent {
  @ViewChild('dialogContainer', { static: true })
  dialogContainer!: ElementRef<HTMLDivElement>;

  form: FormGroup;
  availableModels: ComparePickerModel[] = [];
  readonly catalogCategories: ClariChatModelCategory[];
  leftList: ComparePickerModel[] = [];
  rightList: ComparePickerModel[] = [];

  private originalSelected1: string | null;
  private originalSelected2: string | null;
  tempSelected1: string | null;
  tempSelected2: string | null;

  constructor(
    @Inject(MAT_DIALOG_DATA)
    private data: {
      form: FormGroup;
      availableModels: ComparePickerModel[];
      categories?: ClariChatModelCategory[];
    },
    public dialogRef: MatDialogRef<ModelSelectionDialogComponent>
  ) {
    this.form = data.form;
    const categories = data.categories?.length ? data.categories : CLARI_CHAT_MODEL_CATEGORIES;
    this.catalogCategories = categories
      .map((category) => ({
        ...category,
        models: category.models.filter((model) => !this.isExcludedModel(model.value))
      }))
      .filter((category) => category.models.length > 0);

    this.availableModels = data.availableModels
      .filter((model) => !this.isExcludedModel(model.id))
      .map((model) => ({ ...model, id: this.normalizeModelId(model.id) }));

    this.originalSelected1 = this.getAllowedSelectedModel(this.form.get('selectedModel')?.value || null);
    this.originalSelected2 = this.getAllowedSelectedModel(this.form.get('selectedModel2')?.value || null);
    this.tempSelected1 = this.originalSelected1;
    this.tempSelected2 = this.originalSelected2;

    if (this.originalSelected1) {
      const m1 = this.availableModels.find((m) => this.normalizeModelId(m.id) === this.originalSelected1);
      if (m1) {
        this.leftList = [m1];
        this._removeFromAvailable(m1.id);
      }
    }

    if (this.originalSelected2) {
      const m2 = this.availableModels.find((m) => this.normalizeModelId(m.id) === this.originalSelected2);
      if (m2) {
        this.rightList = [m2];
        this._removeFromAvailable(m2.id);
      }
    }
  }

  onDragMoved(event: CdkDragMove) {
    if (!this.dialogContainer) {
      return;
    }

    const container = this.dialogContainer.nativeElement;
    const { y } = event.pointerPosition;
    const rect = container.getBoundingClientRect();

    const edgeSize = 60;
    const scrollSpeed = 15;

    if (y < rect.top + edgeSize) {
      container.scrollTop -= scrollSpeed;
    } else if (y > rect.bottom - edgeSize) {
      container.scrollTop += scrollSpeed;
    }
  }

  /** Pool ordered like the AI catalog (category order), then any extras. */
  get orderedAvailable(): ComparePickerModel[] {
    const seen = new Set<string>();
    const out: ComparePickerModel[] = [];

    for (const cat of this.catalogCategories) {
      for (const cm of cat.models) {
        const id = this.normalizeModelId(cm.value);
        const row = this.availableModels.find((m) => this.normalizeModelId(m.id) === id);
        if (row && !seen.has(id)) {
          seen.add(id);
          out.push(row);
        }
      }
    }

    for (const row of this.availableModels) {
      const id = this.normalizeModelId(row.id);
      if (!seen.has(id)) {
        seen.add(id);
        out.push(row);
      }
    }

    return out;
  }

  drop(event: CdkDragDrop<ComparePickerModel[]>) {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      const targetList = event.container.data;
      const prevList = event.previousContainer.data;

      if (event.container.id !== 'availableList' && targetList.length > 0) {
        const removed = targetList.splice(0, 1)[0];
        this.availableModels.push(removed);
        if (event.container.id === 'leftList') {
          this.tempSelected1 = null;
        }
        if (event.container.id === 'rightList') {
          this.tempSelected2 = null;
        }
      }

      transferArrayItem(prevList, targetList, event.previousIndex, 0);

      if (event.container.id === 'leftList') {
        this.tempSelected1 = targetList[0]?.id || null;
      } else if (event.container.id === 'rightList') {
        this.tempSelected2 = targetList[0]?.id || null;
      } else if (event.container.id === 'availableList') {
        if (event.previousContainer.id === 'leftList') {
          this.tempSelected1 = null;
        } else if (event.previousContainer.id === 'rightList') {
          this.tempSelected2 = null;
        }
      }
    }
  }

  apply(): void {
    if (!this.tempSelected1 || !this.tempSelected2) {
      alert('Please choose a model for both columns.');
      return;
    }
    const left = this.normalizeModelId(this.tempSelected1);
    const right = this.normalizeModelId(this.tempSelected2);
    this.form.get('selectedModel')!.setValue(left);
    this.form.get('selectedModel2')!.setValue(right);
    this.dialogRef.close({ left, right });
  }

  cancel(): void {
    this.dialogRef.close();
  }

  private _removeFromAvailable(idOrModel: string | ComparePickerModel): void {
    const id = typeof idOrModel === 'string' ? idOrModel : idOrModel.id;
    const norm = this.normalizeModelId(id);
    const idx = this.availableModels.findIndex((m) => this.normalizeModelId(m.id) === norm);
    if (idx > -1) {
      this.availableModels.splice(idx, 1);
    }
  }

  private normalizeModelId(value?: string | null): string {
    return normalizeFlamePickerModelId(value);
  }

  private isExcludedModel(value?: string | null): boolean {
    return EXCLUDED_MODEL_IDS.has(this.normalizeModelId(value));
  }

  private getAllowedSelectedModel(value?: string | null): string | null {
    return this.isExcludedModel(value) ? null : this.normalizeModelId(value);
  }
}
